import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

// Estilos globales de Bitácoras
import "./BitacoraProduccion.css";
import "./BitacoraEnvasado.css";

// Material UI Icons
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import ScienceIcon from '@mui/icons-material/Science';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';

// Redux Ducks de Almacén y Envasado (para Reproceso, Maduración y Condiciones)
import {
  obtenerGranelesAlmacenPorLogin,
  obtenerTodosGranelesAlmacenPorLogin,
  registrarGranelAlmacen,
  actualizarGranelAlmacen,
  eliminarGranelAlmacen,
  registrarFusionGranelAlmacen,
  registrarTrasladoGranelAlmacen,
  registrarDilucionGranelAlmacen,
  registrarBajaGranelAlmacen,
  registrarMuestreoGranelAlmacen,
  obtenerReprocesosAlmacenPorLogin,
  registrarReprocesoAlmacen,
  actualizarReprocesoAlmacen,
  eliminarReprocesoAlmacen,
  obtenerMaduracionAlmacenPorLogin,
  registrarMaduracionAlmacen,
  actualizarMaduracionAlmacen,
  eliminarMaduracionAlmacen,
  obtenerCondicionesAlmacenPorLogin,
  registrarCondicionesAlmacen,
  actualizarCondicionesAlmacen,
  eliminarCondicionesAlmacen
} from "../../../redux/DucksBeneficiario/BitacoraAlmacenDucks";

import { obtenerCatalogoMaguey } from "../../../redux/DucksBeneficiario/BitacoraProduccionDucks";

// Componentes de Secciones
import InventarioGranelSection from "./BitacoraAlmacen/InventarioGranelSection";
import ReprocesoSection from "./BitacoraEnvasado/ReprocesoSection";
import InventarioMaduracionSection from "./BitacoraEnvasado/InventarioMaduracionSection";
import CondicionesSection from "./BitacoraEnvasado/CondicionesSection";

const BitacoraAlmacen = () => {
  const dispatch = useDispatch();

  // Usuario autenticado
  const usuarioData = useSelector(state => state.Usuario?.data || state.Usuario || null);
  const idLogin = usuarioData?.user?.[0]?.id ||
    usuarioData?.user?.id ||
    usuarioData?.idlogin ||
    usuarioData?.id ||
    3462;

  // Pestaña activa: 0 = Granel, 1 = Reproceso, 2 = Maduración, 3 = Condiciones
  const [activeTab, setActiveTab] = useState(0);

  // Configuración de pestañas (Tabs)
  const tabsInfo = [
    { id: 0, title: "Sección II", name: "Granel", desc: "Inventarios de Mezcal a Granel", icon: LocalBarIcon, shortLabel: "II. Granel", theme: "status-cierre" },
    { id: 1, title: "Sección VI", name: "Reproceso", desc: "Abocado, destilado con o reprocesos", icon: ScienceIcon, shortLabel: "VI. Reproceso", theme: "status-elaboracion" },
    { id: 2, title: "Sección VII", name: "Maduración", desc: "Inventarios en Maduración", icon: HourglassBottomIcon, shortLabel: "VII. Maduración", theme: "status-maduracion" },
    { id: 3, title: "Sección VIII", name: "Condiciones", desc: "Registro de Condiciones Ambientales", icon: DeviceThermostatIcon, shortLabel: "VIII. Condiciones", theme: "status-cierre" }
  ];

  // Estado Redux Almacén (Granel)
  const reduxGranelRecords = useSelector(state => state.bitacoraAlmacen?.granelRecords || []);
  const reduxAllGranelRecords = useSelector(state => state.bitacoraAlmacen?.allGranelRecords || []);
  const loadingAlmacen = useSelector(state => state.bitacoraAlmacen?.loading || false);

  // Catálogo de Maguey
  const reduxCatalogoMaguey = useSelector(state => state.bitacoraProduccion?.catalogoMaguey || []);

  // Instalaciones autorizadas del asociado
  const reduxBitacoraInst = useSelector(state => state.bitacoraProduccion?.instalaciones || []);
  const reduxFC36Inst = useSelector(state => state.FC_36Beneficiario?.instalacionesCliente || []);
  const reduxFC35Inst = useSelector(state => state.FC_35Beneficiario?.instalacionesCliente || []);
  const reduxSolicitudesInst = useSelector(state => state.Solicitudes?.instalacionesCliente || []);
  const reduxInstalacionInst = useSelector(state => state.Instalacion?.instalacionesCliente || []);

  const allAvailableInstalaciones = useMemo(() => {
    const list = [
      ...(Array.isArray(reduxBitacoraInst) ? reduxBitacoraInst : []),
      ...(Array.isArray(reduxFC36Inst) ? reduxFC36Inst : []),
      ...(Array.isArray(reduxFC35Inst) ? reduxFC35Inst : []),
      ...(Array.isArray(reduxSolicitudesInst) ? reduxSolicitudesInst : []),
      ...(Array.isArray(reduxInstalacionInst) ? reduxInstalacionInst : [])
    ];

    const map = new Map();
    list.forEach(item => {
      if (!item) return;
      const idAnexo = item.idanexocliente || item.id_anexo_cliente || item.id || item.id_unidad_produccion;
      if (!idAnexo || map.has(String(idAnexo))) return;

      const infoGen = item.informaciongeneralinstalacioncliente || item;
      const alias = infoGen?.alias || item.alias || `Instalación #${idAnexo}`;
      const tipo = item.tipo || infoGen?.tipo || (item.idtipoanexo === 3 ? 'Unidad de almacén' : item.idtipoanexo === 2 ? 'Unidad de envasado' : item.idtipoanexo === 1 ? 'Unidad de producción' : 'Instalación');

      let dir = infoGen?.direccion || item.direccion;
      if (!dir && infoGen) {
        dir = [
          infoGen.calle,
          infoGen.numeroexterior || infoGen.numext,
          infoGen.colonia,
          infoGen.municipio,
          infoGen.estado
        ].filter(Boolean).join(", ");
      }
      dir = dir || "Domicilio registrado";

      map.set(String(idAnexo), {
        ...item,
        idanexocliente: idAnexo,
        id: idAnexo,
        alias: alias,
        direccion: dir,
        tipo: tipo,
        idtipoanexo: item.idtipoanexo || (tipo.toLowerCase().includes("almac") ? 3 : tipo.toLowerCase().includes("envasad") ? 2 : 1),
        informaciongeneralinstalacioncliente: {
          ...infoGen,
          alias: alias,
          direccion: dir,
          tipo: tipo
        }
      });
    });

    return Array.from(map.values());
  }, [reduxBitacoraInst, reduxFC36Inst, reduxFC35Inst, reduxSolicitudesInst, reduxInstalacionInst]);

  const almacenInstalaciones = useMemo(() => {
    const list = allAvailableInstalaciones.filter(inst => {
      const idTipo = Number(inst.idtipoanexo);
      const tipo = String(inst.tipo || inst.informaciongeneralinstalacioncliente?.tipo || '').toLowerCase();
      const alias = String(inst.alias || inst.informaciongeneralinstalacioncliente?.alias || '').toLowerCase();
      return idTipo === 3 || tipo.includes('almac') || alias.includes('almac');
    });
    return list.length > 0 ? list : allAvailableInstalaciones;
  }, [allAvailableInstalaciones]);

  // Estados Redux para Reprocesos, Maduración y Condiciones
  const reduxReprocesos = useSelector(state => state.bitacoraAlmacen?.reprocesoRecords || []);
  const reduxMaduracionRecords = useSelector(state => state.bitacoraAlmacen?.maduracionRecords || []);
  const reduxCondicionesRecords = useSelector(state => state.bitacoraAlmacen?.condicionesRecords || []);

  // Estados locales para formularios
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    id_bitacora_granel: null,
    fecha: new Date().toISOString().split("T")[0],
    fechaMovimiento: new Date().toISOString().split("T")[0],
    instalacion: "Instalación Principal",
    id_unidad_produccion: "",
    id_anexo_cliente: "",
    no_lote: "",
    noLote: "",
    no_tanque: "",
    idContenedor: "",
    categoria: "Mezcal Artesanal",
    clase: "Blanco Joven",
    especie: "Agave Espadín (A. angustifolia)",
    tipoMaguey: "Agave Espadín (A. angustifolia)",
    ingrediente: "",
    clave_fq: "",
    litros: "",
    porcentaje_alc_vol: "45.0",
    no_barricas: "",
    observaciones: "",
    status: "borrador",
    estatus: "borrador"
  });

  // Estados locales para Reproceso
  const [isReprocesoFormOpen, setIsReprocesoFormOpen] = useState(false);
  const [reprocesoFormData, setReprocesoFormData] = useState({});

  // Estados locales para Maduración
  const [isMaduracionFormOpen, setIsMaduracionFormOpen] = useState(false);
  const [maduracionFormData, setMaduracionFormData] = useState({
    fechaMovimiento: new Date().toISOString().split("T")[0],
    noLote: "",
    categoria: "Mezcal Artesanal",
    clase: "Blanco Joven",
    tipoMaguey: "",
    noAnalisis: "",
    observaciones: "",
    recipientes: []
  });

  // Estados locales para Condiciones
  const [isCondicionesFormOpen, setIsCondicionesFormOpen] = useState(false);
  const [condicionesFormData, setCondicionesFormData] = useState({});

  // Carga de datos de todas las secciones
  const cargarDatos = useCallback(() => {
    dispatch(obtenerCatalogoMaguey());
    if (idLogin) {
      dispatch(obtenerGranelesAlmacenPorLogin(idLogin));
      dispatch(obtenerTodosGranelesAlmacenPorLogin(idLogin));
      dispatch(obtenerReprocesosAlmacenPorLogin(idLogin));
      dispatch(obtenerMaduracionAlmacenPorLogin(idLogin));
      dispatch(obtenerCondicionesAlmacenPorLogin(idLogin));
    }
  }, [dispatch, idLogin]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    cargarDatos();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const [editingGranelId, setEditingGranelId] = useState(null);

  const handleOpenNewGranelRecord = () => {
    setEditingGranelId(null);
    setFormData({
      id: null,
      id_bitacora_granel: null,
      fechaMovimiento: new Date().toISOString().split("T")[0],
      fecha: new Date().toISOString().split("T")[0],
      idContenedor: "",
      no_tanque: "",
      noLote: "",
      no_lote: "",
      categoria: "Mezcal Artesanal",
      clase: "Blanco Joven",
      especie: "",
      tipoMaguey: "",
      ingrediente: "",
      ingredientes: "",
      clave_fq: "",
      noAnalisis: "",
      litros: "",
      porcentaje_alc_vol: "",
      invInicialVol: "0",
      invInicialAlc: "0",
      entradaProcedencia: "Ingreso a Almacén",
      entradaVolumen: "0",
      entradaAlc: "0",
      salidaDestino: "",
      salidaVolumen: "0",
      salidaAlc: "0",
      invFinalVol: "0",
      invFinalAlc: "0",
      observaciones: "",
      status: "borrador"
    });
    setIsFormOpen(true);
  };

  const handleCancelGranel = () => {
    setIsFormOpen(false);
    setEditingGranelId(null);
    setFormData({});
  };

  const handleEditGranelRecord = (rec) => {
    if (rec) {
      const rawSt = String(rec.status || rec.statusUI || rec.estatus || '').toLowerCase();
      const isAprobado = rawSt.includes('aprobad') || rawSt.includes('aceptad') || rawSt === 'activo';
      if (isAprobado) {
        Swal.fire({
          icon: 'info',
          title: 'Registro Dictaminado',
          text: 'Este lote a granel ya fue aprobado por la Unidad de Inspección y se encuentra bloqueado para edición directa.',
          confirmButtonColor: '#059669'
        });
        return;
      }
      setEditingGranelId(rec.id);
      const vol = String(rec.litros ?? rec.invFinalVol ?? rec.entradaVolumen ?? "0").replace(/[^\d.]/g, "");
      const alc = String(rec.porcentaje_alc_vol ?? rec.invFinalAlc ?? rec.entradaAlc ?? "0").replace(/[^\d.]/g, "");
      setFormData({
        id: rec.id || rec.dbId || rec.id_bitacora_granel,
        id_bitacora_granel: rec.id_bitacora_granel || rec.dbId || (typeof rec.id === "number" ? rec.id : null),
        dbId: rec.id_bitacora_granel || rec.dbId || (typeof rec.id === "number" ? rec.id : null),
        fechaMovimiento: rec.fechaMovimiento || rec.fecha || new Date().toISOString().split("T")[0],
        fecha: rec.fecha || rec.fechaMovimiento || new Date().toISOString().split("T")[0],
        idContenedor: rec.idContenedor || rec.no_tanque || "",
        no_tanque: rec.no_tanque || rec.idContenedor || "",
        noLote: rec.noLote || rec.no_lote || "",
        no_lote: rec.no_lote || rec.noLote || "",
        categoria: rec.categoria || "Mezcal Artesanal",
        clase: rec.clase || "Blanco Joven",
        ingredientes: rec.ingredientes || rec.ingrediente || "",
        ingrediente: rec.ingrediente || rec.ingredientes || "",
        tipoMaguey: rec.tipoMaguey || rec.especie || "",
        especie: rec.especie || rec.tipoMaguey || "",
        noAnalisis: rec.noAnalisisFisicoquimico || rec.noAnalisis || rec.clave_fq || "",
        clave_fq: rec.clave_fq || rec.noAnalisisFisicoquimico || rec.noAnalisis || "",
        litros: vol,
        porcentaje_alc_vol: alc,
        invInicialVol: String(rec.invInicialVol || vol).replace(/[^\d.]/g, ""),
        invInicialAlc: String(rec.invInicialAlc || alc).replace(/[^\d.]/g, ""),
        entradaProcedencia: rec.entradaProcedencia || "",
        entradaVolumen: vol,
        entradaAlc: alc,
        salidaDestino: rec.salidaDestino || "",
        salidaVolumen: String(rec.salidaVolumen || "0").replace(/[^\d.]/g, ""),
        salidaAlc: String(rec.salidaAlc || "0").replace(/[^\d.]/g, ""),
        invFinalVol: vol,
        invFinalAlc: alc,
        id_unidad_produccion: rec.id_unidad_produccion || rec.id_anexo_cliente,
        id_anexo_cliente: rec.id_anexo_cliente || rec.id_unidad_produccion,
        instalacion: rec.instalacion,
        observaciones: rec.observaciones || "",
        status: rec.status || rec.estatus || "revision"
      });
      setIsFormOpen(true);
    }
  };

  // ==========================================
  // OPERACIONES DE SECCIÓN II: GRANEL
  // ==========================================

  // 1. Guardar Lote Directo (Nuevo o Edición - Borrador o Revisión UI)
  const handleSaveGranel = async (statusOrEvent, customStatus = "revision", optionalData = null) => {
    if (statusOrEvent && statusOrEvent.preventDefault) statusOrEvent.preventDefault();
    const activeData = optionalData || (typeof customStatus === "object" ? customStatus : null) || formData || {};
    const finalStatus = typeof customStatus === "string" ? customStatus : (activeData.status || activeData.estatus || "revision");

    try {
      const payload = {
        ...activeData,
        id_login: idLogin,
        estatus: finalStatus,
        status: finalStatus,
        no_lote: activeData.no_lote || activeData.noLote || "",
        no_tanque: activeData.no_tanque || activeData.idContenedor || "",
        fecha: activeData.fecha || activeData.fechaMovimiento || new Date().toISOString().split("T")[0],
        fechaMovimiento: activeData.fecha || activeData.fechaMovimiento || new Date().toISOString().split("T")[0],
        categoria: activeData.categoria || "Mezcal Artesanal",
        clase: activeData.clase || "Blanco Joven",
        especie: activeData.especie || activeData.tipoMaguey || "",
        tipoMaguey: activeData.especie || activeData.tipoMaguey || "",
        ingrediente: activeData.ingrediente || activeData.ingredientes || "",
        clave_fq: activeData.clave_fq || (activeData.noAnalisis !== "-" ? activeData.noAnalisis : "") || "",
        litros: activeData.litros !== undefined && activeData.litros !== "" ? Number(activeData.litros) : 0,
        porcentaje_alc_vol: activeData.porcentaje_alc_vol !== undefined && activeData.porcentaje_alc_vol !== "" ? Number(activeData.porcentaje_alc_vol) : 0,
        no_barricas: activeData.no_barricas || activeData.noBarricas || "",
        id_unidad_produccion: activeData.id_unidad_produccion || activeData.id_anexo_cliente || null,
        id_anexo_cliente: activeData.id_anexo_cliente || activeData.id_unidad_produccion || null,
        instalacion: activeData.instalacion || "Instalación Principal"
      };

      const targetId = activeData.id_bitacora_granel || (typeof activeData.id === "number" ? activeData.id : null);
      let resAction;
      if (targetId) {
        resAction = await dispatch(actualizarGranelAlmacen({ id: targetId, ...payload }));
      } else {
        resAction = await dispatch(registrarGranelAlmacen(payload));
      }

      if (resAction?.error) {
        Swal.fire("Error", resAction.payload || resAction.error?.message || "No se pudo guardar el lote", "error");
        return;
      }

      setIsFormOpen(false);
      cargarDatos();
      Swal.fire({
        title: "¡Éxito!",
        text: finalStatus === "borrador" ? "Lote a granel guardado como borrador." : "Lote a granel registrado y enviado a revisión de la Unidad de Inspección.",
        icon: "success",
        confirmButtonColor: "#15803D"
      });
    } catch (err) {
      console.error("Error al guardar granel de almacén:", err);
      Swal.fire("Error", "Ocurrió un error inesperado al guardar el lote.", "error");
    }
  };

  // 2. Fusión / Extracción
  const handleSaveFusion = async (newGranelData, validSources, finalStatus = "revision") => {
    try {
      let payloadBackend = {};
      if (validSources && Array.isArray(validSources)) {
        payloadBackend = {
          id_login: idLogin,
          no_lote: newGranelData.noLote || newGranelData.no_lote,
          no_tanque: newGranelData.idContenedor || newGranelData.no_tanque,
          categoria: newGranelData.categoria,
          clase: newGranelData.clase,
          especie: newGranelData.tipoMaguey || newGranelData.especie,
          ingrediente: newGranelData.ingredientes || newGranelData.ingrediente,
          litros: parseFloat(newGranelData.invFinalVol || newGranelData.litros) || 0,
          porcentaje_alc_vol: parseFloat(newGranelData.invFinalAlc || newGranelData.porcentaje_alc_vol) || 0,
          fecha: newGranelData.fechaMovimiento || newGranelData.fecha || new Date().toISOString(),
          estatus: finalStatus,
          observaciones: newGranelData.observaciones || "",
          fuentes: validSources.map(s => ({
            id_granel_origen: s.id_granel_origen || s.id || s.granelId,
            granelId: s.granelId,
            volExtraer: parseFloat(s.volExtraer) || 0,
            no_lote_origen: s.no_lote_origen || s.noLote || s.granelId
          }))
        };
      } else {
        payloadBackend = { ...newGranelData, id_login: idLogin };
      }

      const resAction = await dispatch(registrarFusionGranelAlmacen(payloadBackend));

      if (resAction?.error) {
        Swal.fire("Error", resAction.payload || resAction.error?.message || "No se pudo procesar la fusión", "error");
        return;
      }

      cargarDatos();
      Swal.fire({
        title: "¡Fusión Procesada!",
        text: "La fusión / extracción se aplicó y descontó de los lotes fuente exitosamente.",
        icon: "success",
        confirmButtonColor: "#7C3AED"
      });
    } catch (err) {
      console.error("Error en fusión de almacén:", err);
      Swal.fire("Error", "Ocurrió un error al procesar la fusión.", "error");
    }
  };

  // 3. Traslado entre Instalaciones
  const handleSaveTraslado = async (trasladoData) => {
    try {
      const resAction = await dispatch(registrarTrasladoGranelAlmacen({
        ...trasladoData,
        id_login: idLogin
      }));

      if (resAction?.error) {
        Swal.fire("Error", resAction.payload || resAction.error?.message || "No se pudo registrar el traslado", "error");
        return;
      }

      cargarDatos();
      Swal.fire({
        title: "¡Traslado Registrado!",
        text: `El traslado hacia ${trasladoData.instalacion_destino} fue procesado correctamente.`,
        icon: "success",
        confirmButtonColor: "#1D4ED8"
      });
    } catch (err) {
      console.error("Error en traslado de almacén:", err);
      Swal.fire("Error", "Ocurrió un error al procesar el traslado.", "error");
    }
  };

  // 4. Ajuste / Dilución
  const handleSaveDilucion = async (dilucionData) => {
    try {
      const resAction = await dispatch(registrarDilucionGranelAlmacen({
        ...dilucionData,
        id_login: idLogin
      }));

      if (resAction?.error) {
        Swal.fire("Error", resAction.payload || resAction.error?.message || "No se pudo registrar la dilución", "error");
        return;
      }

      cargarDatos();
      Swal.fire({
        title: "¡Dilución Aplicada!",
        text: "El grado alcohólico y volumen del tanque fueron ajustados con éxito.",
        icon: "success",
        confirmButtonColor: "#0D9488"
      });
    } catch (err) {
      console.error("Error en dilución de almacén:", err);
      Swal.fire("Error", "Ocurrió un error al aplicar la dilución.", "error");
    }
  };

  // 5. Baja / Merma
  const handleSaveBaja = async (bajaData) => {
    try {
      const resAction = await dispatch(registrarBajaGranelAlmacen({
        ...bajaData,
        id_login: idLogin
      }));

      if (resAction?.error) {
        Swal.fire("Error", resAction.payload || resAction.error?.message || "No se pudo registrar la baja", "error");
        return;
      }

      cargarDatos();
      Swal.fire({
        title: "¡Baja Registrada!",
        text: "El volumen fue descontado del inventario correctamente.",
        icon: "success",
        confirmButtonColor: "#D97706"
      });
    } catch (err) {
      console.error("Error en baja de almacén:", err);
      Swal.fire("Error", "Ocurrió un error al registrar la baja.", "error");
    }
  };

  // 6. Muestreo Oficial NOM-070
  const handleSaveMuestreo = async (muestreoData) => {
    try {
      const resAction = await dispatch(registrarMuestreoGranelAlmacen({
        ...muestreoData,
        id_login: idLogin
      }));

      if (resAction?.error) {
        Swal.fire("Error", resAction.payload || resAction.error?.message || "No se pudo enviar la solicitud de muestreo", "error");
        return;
      }

      cargarDatos();
      Swal.fire({
        title: "¡Solicitud de Muestreo Enviada!",
        text: "La solicitud de muestreo fue enviada a la Unidad de Inspección para su visita técnica.",
        icon: "success",
        confirmButtonColor: "#4F46E5"
      });
    } catch (err) {
      console.error("Error en muestreo de almacén:", err);
      Swal.fire("Error", "Ocurrió un error al registrar el muestreo.", "error");
    }
  };

  // ==========================================
  // ==========================================
  // OPERACIONES DE SECCIÓN VI: REPROCESO
  // ==========================================
  const handleSaveReproceso = async (payload) => {
    try {
      const isEdit = payload.id_bitacora_reproceso || (payload.id && !String(payload.id).startsWith("REP-"));
      if (isEdit) {
        const idReproceso = payload.id_bitacora_reproceso || payload.id;
        await dispatch(actualizarReprocesoAlmacen({ id_bitacora_reproceso: idReproceso, data: payload })).unwrap();
        Swal.fire({
          icon: "success",
          title: "Actualizado",
          text: "El registro de reproceso ha sido actualizado exitosamente.",
          confirmButtonColor: "#2563eb"
        });
      } else {
        await dispatch(registrarReprocesoAlmacen({ ...payload, id_login: idLogin })).unwrap();
        Swal.fire({
          icon: "success",
          title: "Registrado",
          text: "El proceso de reproceso ha sido registrado exitosamente.",
          confirmButtonColor: "#16a34a"
        });
      }
      dispatch(obtenerReprocesosAlmacenPorLogin(idLogin));
      cargarDatos();
    } catch (err) {
      console.error("Error guardando reproceso:", err);
      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: typeof err === "string" ? err : (err?.message || "Ocurrió un error al procesar el reproceso."),
        confirmButtonColor: "#dc2626"
      });
    }
  };

  const handleDeleteReproceso = async (rec) => {
    const idReproceso = rec.id_bitacora_reproceso || rec.id;
    const confirm = await Swal.fire({
      title: "¿Eliminar registro?",
      text: `¿Está seguro de eliminar el registro de reproceso para el lote ${rec.loteSalida || rec.lote_salida || ""}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar"
    });

    if (confirm.isConfirmed) {
      try {
        await dispatch(eliminarReprocesoAlmacen(idReproceso)).unwrap();
        Swal.fire("Eliminado", "El registro fue eliminado correctamente.", "success");
        dispatch(obtenerReprocesosAlmacenPorLogin(idLogin));
        cargarDatos();
      } catch (err) {
        console.error("Error eliminando reproceso:", err);
        Swal.fire("Error", "No se pudo eliminar el registro.", "error");
      }
    }
  };

  // ==========================================
  // OPERACIONES DE SECCIÓN VII: MADURACIÓN
  // ==========================================
  const handleSaveMaduracion = async (record) => {
    try {
      const isExisting = record.dbId || (record.id_bitacora_maduracion && !isNaN(Number(record.id_bitacora_maduracion))) || (typeof record.id === "number");
      const targetId = record.dbId || record.id_bitacora_maduracion || record.id;

      const payload = {
        ...record,
        id_login: idLogin
      };

      let resultAction;
      if (isExisting && targetId) {
        resultAction = await dispatch(actualizarMaduracionAlmacen({ id: targetId, maduracionData: payload }));
      } else {
        resultAction = await dispatch(registrarMaduracionAlmacen(payload));
      }

      if (resultAction?.error) {
        Swal.fire("Error", resultAction.payload || resultAction.error?.message || "No se pudo guardar la maduración", "error");
        return;
      }

      if (idLogin) {
        dispatch(obtenerMaduracionAlmacenPorLogin(idLogin));
        dispatch(obtenerGranelesAlmacenPorLogin(idLogin));
      }

      setIsMaduracionFormOpen(false);
      Swal.fire({
        title: "¡Éxito!",
        text: "Registro de Maduración (Sección VII) guardado correctamente.",
        icon: "success",
        confirmButtonColor: "#D97706"
      });
    } catch (err) {
      console.error("Error al guardar maduración:", err);
      Swal.fire("Error", "Ocurrió un error inesperado al guardar la maduración", "error");
    }
  };

  const handleDeleteMaduracion = async (rec) => {
    const targetId = rec.dbId || rec.id_bitacora_maduracion || rec.id;
    const confirm = await Swal.fire({
      title: "¿Eliminar registro de maduración?",
      text: `¿Desea eliminar la bitácora del lote ${rec.noLote || rec.no_lote}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar"
    });

    if (confirm.isConfirmed) {
      try {
        await dispatch(eliminarMaduracionAlmacen(targetId));
        if (idLogin) {
          dispatch(obtenerMaduracionAlmacenPorLogin(idLogin));
          dispatch(obtenerGranelesAlmacenPorLogin(idLogin));
        }
        Swal.fire("Eliminado", "Registro de maduración eliminado con éxito.", "success");
      } catch (err) {
        console.error("Error al eliminar maduración:", err);
        Swal.fire("Error", "No se pudo eliminar el registro", "error");
      }
    }
  };

  // ==========================================
  // OPERACIONES DE SECCIÓN VIII: CONDICIONES
  // ==========================================
  const handleSaveCondiciones = async (record) => {
    try {
      const isExisting = record.dbId || (record.id_bitacora_condiciones && !isNaN(Number(record.id_bitacora_condiciones))) || (typeof record.id === "number");
      const targetId = record.dbId || record.id_bitacora_condiciones || record.id;

      const payload = {
        ...record,
        id_login: idLogin
      };

      let resultAction;
      if (isExisting && targetId) {
        resultAction = await dispatch(actualizarCondicionesAlmacen({ id: targetId, ...payload }));
      } else {
        resultAction = await dispatch(registrarCondicionesAlmacen(payload));
      }

      if (resultAction?.error) {
        Swal.fire("Error", resultAction.payload || resultAction.error?.message || "No se pudo guardar el registro de condiciones", "error");
        return;
      }

      if (idLogin) {
        dispatch(obtenerCondicionesAlmacenPorLogin(idLogin));
      }

      setIsCondicionesFormOpen(false);
      Swal.fire({
        title: "¡Éxito!",
        text: "Registro de Condiciones Ambientales de Maduración guardado correctamente.",
        icon: "success",
        confirmButtonColor: "#2563EB"
      });
    } catch (err) {
      console.error("Error al guardar condiciones ambientales:", err);
      Swal.fire("Error", "Ocurrió un error inesperado al guardar las condiciones", "error");
    }
  };

  const handleDeleteCondiciones = async (id) => {
    const confirm = await Swal.fire({
      title: "¿Eliminar registro de condiciones?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar"
    });

    if (confirm.isConfirmed) {
      try {
        await dispatch(eliminarCondicionesAlmacen(id));
        if (idLogin) dispatch(obtenerCondicionesAlmacenPorLogin(idLogin));
        Swal.fire("Eliminado", "Registro de condiciones ambientales eliminado con éxito.", "success");
      } catch (err) {
        console.error("Error al eliminar condiciones:", err);
        Swal.fire("Error", "No se pudo eliminar el registro", "error");
      }
    }
  };

  const handleReloadReproceso = async () => {
    if (idLogin) {
      await dispatch(obtenerReprocesosAlmacenPorLogin(idLogin));
      cargarDatos();
    }
  };

  const handleReloadMaduracion = async () => {
    if (idLogin) {
      await dispatch(obtenerMaduracionAlmacenPorLogin(idLogin));
      cargarDatos();
    }
  };

  const handleReloadCondiciones = () => {
    if (idLogin) dispatch(obtenerCondicionesAlmacenPorLogin(idLogin));
  };

  return (
    <div className="container2">
      {/* Decorative Blurs */}
      <div className="glow-blob glow-1"></div>
      <div className="glow-blob glow-2"></div>

      {/* HEADER DE TRAZABILIDAD DE ALMACÉN */}
      <header className="main-trace-header">
        <div className="header-branding">
          <div className="header-icon-box" style={{ background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)", boxShadow: "0 8px 18px rgba(29, 78, 216, 0.25)" }}>
            <WarehouseIcon style={{ fontSize: 30 }} />
          </div>
          <div>
            <div className="header-tag">COMERCAM • TRAZABILIDAD DE ALMACÉN</div>
            <h1>Bitácora de Almacén de Mezcal</h1>
            <p>Control integral de inventarios de mezcal a granel, reprocesos, maduración y condiciones ambientales</p>
          </div>
        </div>

        <div className="header-controls-envasado">
          <span className={`status-badge-envasado ${tabsInfo[activeTab]?.theme || 'status-cierre'}`}>
            {tabsInfo[activeTab]?.title} • {tabsInfo[activeTab]?.name}
          </span>
        </div>
      </header>

      {/* PESTAÑAS (SEGMENTED PILLS): GRANEL, REPROCESO, MADURACIÓN, CONDICIONES */}
      <div className="tabs-nav-bar-container">
        <div className="segmented-pill-container">
          {tabsInfo.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                className={`segmented-pill-btn ${isActive ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setIsFormOpen(false);
                }}
              >
                <IconComponent style={{ fontSize: 16 }} />
                <span>{tab.shortLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* CONTENIDO DE LA SECCIÓN ACTIVA */}
      <main className="section-tab-container animate-fadeIn">

        {/* TAB 0: SECCIÓN II - INVENTARIOS DE MEZCAL A GRANEL */}
        {activeTab === 0 && (
          <InventarioGranelSection
            granelRecords={reduxGranelRecords}
            allGranelRecords={reduxAllGranelRecords}
            availableInstalaciones={almacenInstalaciones}
            allAvailableInstalaciones={allAvailableInstalaciones}
            catalogoMaguey={reduxCatalogoMaguey}
            isFormOpen={isFormOpen}
            setIsFormOpen={setIsFormOpen}
            formData={formData}
            setFormData={setFormData}
            handleSaveGranel={handleSaveGranel}
            handleCancelGranel={handleCancelGranel}
            handleEditRecord={handleEditGranelRecord}
            handleOpenNewRecord={handleOpenNewGranelRecord}
            handleSaveFusion={handleSaveFusion}
            handleSaveTraslado={handleSaveTraslado}
            handleSaveDilucion={handleSaveDilucion}
            handleSaveBaja={handleSaveBaja}
            handleSaveMuestreo={handleSaveMuestreo}
            handleRefreshGranel={handleRefresh}
            isRefreshingGranel={isRefreshing || loadingAlmacen}
          />
        )}

        {/* TAB 1: SECCIÓN VI - REPROCESO */}
        {activeTab === 1 && (
          <ReprocesoSection
            reprocesoRecords={reduxReprocesos}
            granelRecords={reduxGranelRecords}
            allGranelRecords={reduxAllGranelRecords}
            isFormOpen={isReprocesoFormOpen}
            setIsFormOpen={setIsReprocesoFormOpen}
            formData={reprocesoFormData}
            setFormData={setReprocesoFormData}
            handleSaveReproceso={handleSaveReproceso}
            handleDeleteReproceso={handleDeleteReproceso}
            onReloadReproceso={handleReloadReproceso}
          />
        )}

        {/* TAB 2: SECCIÓN VII - INVENTARIO EN MADURACIÓN */}
        {activeTab === 2 && (
          <InventarioMaduracionSection
            maduracionRecords={reduxMaduracionRecords}
            granelRecords={reduxGranelRecords}
            isFormOpen={isMaduracionFormOpen}
            setIsFormOpen={setIsMaduracionFormOpen}
            formData={maduracionFormData}
            setFormData={setMaduracionFormData}
            handleSaveMaduracion={handleSaveMaduracion}
            handleDeleteMaduracion={handleDeleteMaduracion}
            onReloadMaduracion={handleReloadMaduracion}
          />
        )}

        {/* TAB 3: SECCIÓN VIII - CONDICIONES DEL ÁREA DE MADURACIÓN */}
        {activeTab === 3 && (
          <CondicionesSection
            tipoBitacora="Almacén"
            condicionesRecords={reduxCondicionesRecords}
            isFormOpen={isCondicionesFormOpen}
            setIsFormOpen={setIsCondicionesFormOpen}
            formData={condicionesFormData}
            setFormData={setCondicionesFormData}
            handleSaveCondiciones={handleSaveCondiciones}
            handleDeleteCondiciones={handleDeleteCondiciones}
            onReloadCondiciones={handleReloadCondiciones}
          />
        )}

      </main>
    </div>
  );
};

export default BitacoraAlmacen;
